import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CanvasBlock } from '@elva/ui';
import { keys } from '@/api/queryKeys';
import {
  adaptFormBlocks,
  buildFormTemplatePayload,
  saveFormTemplate,
  syncFormBlocks,
} from '../adapters/formsAdapters';
import { formsApi } from '../api/formsApi';
import type { FormCondition, FormTemplateDetail, FormTemplateStatus } from '../api/types';

export type FormTemplateQueryData = {
  template: FormTemplateDetail;
  blocks: CanvasBlock[];
  conditions: FormCondition[];
  triggerRules: FormTemplateDetail['trigger_rules'];
};

export function useFormTemplate(formId: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.template(formId ?? ''),
    queryFn: async ({ signal }): Promise<FormTemplateQueryData> => {
      const [detail, blocksResponse, conditionsResponse] = await Promise.all([
        formsApi.getTemplate(formId!, { signal }),
        formsApi.getBlocks(formId!, { signal }),
        formsApi
          .getConditions(formId!, { signal })
          .catch(() => ({ conditions: [] as FormCondition[] })),
      ]);

      const blocks = blocksResponse.blocks.length ? blocksResponse.blocks : (detail.blocks ?? []);
      const conditions = conditionsResponse.conditions.length
        ? conditionsResponse.conditions
        : (detail.conditions ?? []);

      return {
        template: detail,
        blocks: adaptFormBlocks(blocks, conditions),
        conditions,
        triggerRules: detail.trigger_rules ?? [],
      };
    },
    enabled: formId != null && formId !== '',
    staleTime: 60_000,
  });
}

export function useCreateFormTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) => formsApi.createTemplate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useUpdateFormTemplateMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FormTemplateDetail>) => formsApi.updateTemplate(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.blocks(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useUpdateFormBlocksMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { blocks: CanvasBlock[]; conditions?: FormTemplateDetail['conditions'] }) =>
      syncFormBlocks(formId, input.blocks, input.conditions ?? []),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.blocks(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.conditions(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
    },
  });
}

export function useSaveFormBuilderMutation(formId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      formId: string;
      name: string;
      status?: FormTemplateStatus;
      blocks: CanvasBlock[];
      conditions?: FormTemplateDetail['conditions'];
    }) => {
      const payload = buildFormTemplatePayload(input.name, input.status ?? 'draft');
      return saveFormTemplate(
        input.formId,
        payload,
        input.blocks,
        input.conditions ?? [],
      );
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(result.formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.blocks(result.formId) });
      if (formId && formId !== result.formId) {
        void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
      }
    },
  });
}

export function useValidateFormTemplateMutation(formId: string) {
  return useMutation({
    mutationFn: () => formsApi.validateTemplate(formId),
  });
}

export function usePublishFormTemplateMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const validation = await formsApi.validateTemplate(formId);
      if (!validation.valid) {
        throw new Error(validation.errors?.[0]?.message ?? 'Form validation failed');
      }
      return formsApi.updateTemplate(formId, { status: 'active' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useFormAiAssistMutation(formId: string) {
  return useMutation({
    mutationFn: (data: { block_id?: string; prompt?: string }) => formsApi.aiAssist(formId, data),
  });
}
